import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, matchPath, useLocation, type Location } from 'react-router';
import { AppShell } from './AppShell';
import { useServices } from './services';
import { lazyScreen } from './lazyScreen';
import { HomeScreen } from '../features/home/HomeScreen';
import { BudgetsScreen } from '../features/budgets/BudgetsScreen';
import { MoreScreen } from '../features/more/MoreScreen';
import { QuickAddSheet } from '../features/quick-add/QuickAddSheet';
import { EntrySheet } from '../features/entries/EntrySheet';
import { RecurringScreen } from '../features/recurring/RecurringScreen';
import { AccountScreen } from '../features/account/AccountScreen';
import { DocumentDrop } from '../features/import/DocumentDrop';

/** Design-system reference + prototypes: its own chunk, never part of the main bundle. */
const DesignRoutes = lazy(() => import('../features/design/DesignRoutes'));

/**
 * Screens Home does not need load on their own (Phase 26: a 650 kB start
 * bundle kept a phone busy for a second before Home's first query came
 * back). AppShell fetches them in the background once Home is up, so a tab
 * switch never waits on the network or the parser.
 */
const AnalyticsScreen = lazyScreen(
  () => import('../features/analytics/AnalyticsScreen'),
  (m) => m.AnalyticsScreen,
);
const EntriesScreen = lazyScreen(
  () => import('../features/entries/EntriesScreen'),
  (m) => m.EntriesScreen,
);
const OnboardingScreen = lazyScreen(
  () => import('../features/onboarding/OnboardingScreen'),
  (m) => m.OnboardingScreen,
);
const CategoriesScreen = lazyScreen(
  () => import('../features/categories/CategoriesScreen'),
  (m) => m.CategoriesScreen,
);
const CategoryDetailScreen = lazyScreen(
  () => import('../features/categories/CategoryDetailScreen'),
  (m) => m.CategoryDetailScreen,
);
const BudgetDetailScreen = lazyScreen(
  () => import('../features/sharing/BudgetDetailScreen'),
  (m) => m.BudgetDetailScreen,
);
const JoinScreen = lazyScreen(
  () => import('../features/sharing/JoinScreen'),
  (m) => m.JoinScreen,
);
const PremiumScreen = lazyScreen(
  () => import('../premium/PremiumScreen'),
  (m) => m.PremiumScreen,
);
const ImportScreen = lazyScreen(
  () => import('../features/import/ImportScreen'),
  (m) => m.ImportScreen,
);
const ActivityScreen = lazyScreen(
  () => import('../features/activity/ActivityScreen'),
  (m) => m.ActivityScreen,
);

/**
 * Navigation map. Until a budget exists every route leads to onboarding.
 * After that, four tab destinations share one shell (bottom bar on
 * phones, side rail on wide screens). Quick Add is a modal route: opened
 * from the + button it renders over whatever screen you were on (the
 * "background location" pattern), so the Android back button and browser
 * back both simply close it. Opened directly by URL it sits over Home.
 */
export type ModalState = { background?: Location } | null;

const designRoute = (
  <Route
    path="design/*"
    element={
      <Suspense fallback={null}>
        <DesignRoutes />
      </Suspense>
    }
  />
);

export function AppRoutes() {
  const location = useLocation();
  const { activeSpace, platform } = useServices();
  const background = (location.state as ModalState)?.background;
  const showAdd = location.pathname === '/add';
  const showEntry = matchPath('/entry/:id', location.pathname);

  if (!activeSpace) {
    return (
      <Suspense fallback={null}>
        <Routes>
          {designRoute}
          <Route path="onboarding" element={<OnboardingScreen />} />
          {/* Invited on a fresh install: straight into the shared budget. */}
          <Route path="join" element={<JoinScreen standalone />} />
          <Route path="join/:code" element={<JoinScreen standalone />} />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <Routes location={background ?? location}>
        {designRoute}
        <Route path="onboarding" element={<Navigate to="/" replace />} />
        <Route element={<AppShell />}>
          <Route index element={<HomeScreen />} />
          <Route path="analytics" element={<AnalyticsScreen />} />
          <Route path="entries" element={<EntriesScreen />} />
          <Route path="budgets" element={<BudgetsScreen />} />
          <Route path="budgets/:id" element={<BudgetDetailScreen />} />
          <Route path="budgets/:id/activity" element={<ActivityScreen />} />
          <Route path="join" element={<JoinScreen />} />
          <Route path="join/:code" element={<JoinScreen />} />
          <Route path="more" element={<MoreScreen />} />
          <Route path="more/categories" element={<CategoriesScreen />} />
          <Route path="more/categories/:id" element={<CategoryDetailScreen />} />
          <Route path="more/recurring" element={<RecurringScreen />} />
          <Route path="more/account" element={<AccountScreen />} />
          <Route path="import" element={<ImportScreen />} />
          <Route path="premium" element={<PremiumScreen />} />
          <Route path="add" element={<HomeScreen />} />
          <Route path="entry/:id" element={<HomeScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {/* Smart Bill Import: a bill dropped anywhere on the web opens the import screen. */}
      {platform === 'web' && <DocumentDrop />}
      {showAdd && <QuickAddSheet />}
      {showEntry && (
        <Routes>
          <Route path="entry/:id" element={<EntrySheet />} />
        </Routes>
      )}
    </>
  );
}
